import { useLayoutEffect, useRef, useState } from "react";

import { Badge } from "components/ui/badge";
import { buttonVariants } from "components/ui/button";
import { ComboboxTrigger, ComboboxValue } from "components/ui/combobox";
import { cn } from "lib/utils";

const BADGE_MAX_WIDTH = "tw:max-w-32";

function useVisibleBadgeCount(rowRef, ghostRef, total, signature) {
  const [visibleCount, setVisibleCount] = useState(total);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const ghost = ghostRef.current;

    const measure = () => {
      const badges = [...ghost.children];
      const counter = badges.pop();
      // A non-numeric columnGap would turn every comparison below into NaN,
      // which silently disables truncation instead of failing.
      const gap = parseFloat(getComputedStyle(ghost).columnGap) || 0;
      const widths = badges.map(badge => badge.offsetWidth);
      const available = row.clientWidth;

      if (widths.reduce((sum, width) => sum + width + gap, -gap) <= available) {
        setVisibleCount(widths.length);
        return;
      }

      const budget = available - counter.offsetWidth - gap;
      let used = -gap;
      let fit = 0;
      for (const width of widths) {
        if (used + gap + width > budget) break;
        used += gap + width;
        fit += 1;
      }
      // The trigger is measured at width 0 while the filter panel is closed;
      // the observer corrects the count once it opens.
      setVisibleCount(Math.max(1, fit));
    };

    measure();

    // The row catches the trigger resizing; the ghost catches its own content
    // changing width for reasons no dependency can express, such as a web font
    // finishing loading.
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(ghost);
    return () => observer.disconnect();
  }, [rowRef, ghostRef, total, signature]);

  return visibleCount;
}

function SelectedBadges({
  selected,
  labelFor,
  keyFor,
  getTagStyle,
  renderBadgeAddon,
}) {
  const rowRef = useRef(null);
  const ghostRef = useRef(null);
  const visibleCount = useVisibleBadgeCount(
    rowRef,
    ghostRef,
    selected.length,
    selected.map(value => labelFor(value)).join("|")
  );

  const visible = selected.slice(0, visibleCount);
  const overflow = selected.length - visible.length;

  // Shrinking lets a lone oversized badge truncate instead of shoving the
  // counter past the row's clip edge. The ghost copies keep the badge's default
  // shrink-0 so they measure at their natural width, addon included.
  const renderBadge = (value, shrink) => (
    <Badge
      key={keyFor(value)}
      style={getTagStyle?.(value)}
      className={cn(BADGE_MAX_WIDTH, shrink && "tw:min-w-0 tw:shrink")}
    >
      <span className="tw:min-w-0 tw:truncate">{labelFor(value)}</span>
      {renderBadgeAddon?.(value)}
    </Badge>
  );

  // Inline-size containment zeroes the badges' intrinsic width contribution, so
  // an overshooting count can only ever clip inside the row — it can't widen the
  // trigger or wrap the filter card, and the width the row reports back to
  // measure() stays purely a function of the card, not of the badges.
  return (
    <div
      ref={rowRef}
      className="tw:relative tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-1 tw:overflow-hidden tw:contain-[inline-size]"
    >
      <div
        ref={ghostRef}
        aria-hidden
        className="tw:pointer-events-none tw:invisible tw:absolute tw:top-0 tw:left-0 tw:flex tw:w-max tw:items-center tw:gap-1"
      >
        {selected.map(value => renderBadge(value, false))}
        <Badge variant="secondary">+{selected.length}</Badge>
      </div>
      {visible.map(value => renderBadge(value, true))}
      {overflow > 0 && (
        <Badge variant="secondary" className="tw:shrink-0">
          +{overflow}
        </Badge>
      )}
    </div>
  );
}

export function MultipleFilterTrigger({
  labelFor,
  keyFor,
  getTagStyle,
  renderBadgeAddon,
  placeholder = "Select...",
  className,
  ...props
}) {
  return (
    <ComboboxTrigger
      className={buttonVariants({
        variant: "outline",
        // min-w-0 is load-bearing: without it the badges' min-content size
        // becomes the trigger's automatic minimum, so it grows to fit them
        // instead of clipping, and the measured width feeds back into itself.
        className: cn(
          "tw:min-w-0 tw:flex-1 tw:justify-between tw:bg-white tw:dark:bg-background",
          className
        ),
      })}
      {...props}
    >
      <ComboboxValue>
        {selected =>
          selected.length ? (
            <SelectedBadges
              selected={selected}
              labelFor={labelFor}
              keyFor={keyFor}
              getTagStyle={getTagStyle}
              renderBadgeAddon={renderBadgeAddon}
            />
          ) : (
            <span className="tw:text-muted-foreground">{placeholder}</span>
          )
        }
      </ComboboxValue>
    </ComboboxTrigger>
  );
}
